import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/config';
import './SharedStyles.css';

interface DocumentUploadModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = ['docx', 'pdf', 'txt'];
  const maxSize = 50 * 1024 * 1024; // 50MB

  const validateFile = (f: File): string | null => {
    const extension = f.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedTypes.includes(extension)) {
      return `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
    }
    if (f.size > maxSize) {
      return `File size exceeds ${maxSize / (1024 * 1024)}MB limit`;
    }
    return null;
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }
    setError(null);
    setFile(selectedFile);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setProgress(0);
    setStage('Uploading file...');

    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    // 30 min timeout — processing (extract, chunk, embed) can take several minutes
    xhr.timeout = 30 * 60 * 1000;

    // Track file upload progress (0-50% of bar)
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const pct = (e.loaded / e.total) * 50; // file upload = first 50%
        setProgress(pct);
        if (e.loaded === e.total) {
          setStage('Processing document... This may take several minutes.');
          setProgress(50);
        }
      }
    });

    // Server finished processing and responded
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        setProgress(100);
        setStage('Done!');
        setTimeout(() => onSuccess(), 600);
      } else {
        let errorMsg = 'Upload failed. Please try again.';
        try {
          const errData = JSON.parse(xhr.responseText);
          errorMsg = errData.error || errorMsg;
        } catch {
          if (xhr.status === 401) errorMsg = 'Please log in again.';
        }
        setError(errorMsg);
        setUploading(false);
      }
    });

    xhr.addEventListener('timeout', () => {
      setError('Request timed out. The document may still be processing — check Document Library in a moment.');
      setUploading(false);
    });

    xhr.addEventListener('error', () => {
      setError('Upload failed. Please check your connection and try again.');
      setUploading(false);
    });

    xhr.open('POST', `${API_URL}/api/rag/documents/upload`);
    xhr.withCredentials = true;
    const authHeaders = getAuthHeaders();
    Object.keys(authHeaders).forEach((key) => {
      xhr.setRequestHeader(key, authHeaders[key]);
    });
    xhr.send(formData);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Document</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div
            className={`upload-area ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,.pdf,.txt"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {file ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                  {file.name}
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  {formatFileSize(file.size)}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📤</div>
                <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                  Drag and drop a file here
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  or click to browse
                </div>
                <div
                  style={{
                    marginTop: '1rem',
                    fontSize: '0.75rem',
                    color: '#9ca3af',
                  }}
                >
                  Supported: DOCX, PDF, TXT (max {maxSize / (1024 * 1024)}MB)
                </div>
              </div>
            )}
          </div>

          {file && !uploading && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setFile(null);
                setError(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              Remove File
            </button>
          )}

          {error && (
            <div className="alert alert-error" style={{ marginTop: '1rem' }}>
              {error}
            </div>
          )}

          {uploading && (
            <div style={{ marginTop: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.25rem',
                }}
              >
                <span style={{ fontWeight: 500 }}>{stage}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: progress >= 100 ? '#10b981' : '#3b82f6',
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Processing...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
